import { useEffect, useState } from "react";
import { api, BRL, formatApiErrorDetail, authFileUrl, authFileDownloadUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileText, Plus, Trash2, Loader2, Paperclip, Download } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const FREQ = { monthly: "Mensal", biweekly: "Quinzenal", weekly: "Semanal", semiannual: "Semestral" };
const empty = { client_id: "", title: "", description: "", value_total: "", installments: "1", first_due_date: new Date().toISOString().slice(0, 10), frequency: "monthly", cpf: "", pix_key: "", phone: "", category_id: "", category_name: "" };

export default function Contratos() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [attachment, setAttachment] = useState({ path: null, name: null });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = () => api.get("/contracts").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/clients").then((r) => setClients(r.data)); api.get("/categories").then((r) => setCategories(r.data)); }, []);

  const openNew = () => { setForm(empty); setAttachment({ path: null, name: null }); setOpen(true); };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachment({ path: data.path, name: data.filename });
      toast.success("Arquivo anexado");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.title || !form.value_total) return toast.error("Informe título e valor total");
    if (!form.category_id) return toast.error("Selecione uma categoria");
    setSaving(true);
    try {
      const payload = { ...form, value_total: parseFloat(form.value_total), installments: parseInt(form.installments) || 1, attachment_path: attachment.path, attachment_name: attachment.name };
      const { data } = await api.post("/contracts", payload);
      toast.success(`Contrato criado — ${data.generated} parcela(s) gerada(s)`);
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    await api.delete(`/contracts/${toDelete.id}`);
    setToDelete(null);
    toast.success("Contrato e suas parcelas removidos");
    load();
  };

  return (
    <div className="fade-up space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-head text-4xl font-extrabold tracking-tight">Contratos</h1>
          <p className="text-muted-foreground mt-1">Cada contrato gera parcelas (despesas) automaticamente</p>
        </div>
        <Button data-testid="add-contract-button" className="font-semibold gap-2" onClick={openNew}><Plus className="h-4 w-4" /> Novo contrato</Button>
      </div>

      {items.length === 0 ? (
        <Card className="border-border/60 shadow-none rounded-md py-16 text-center" data-testid="contracts-empty">
          <FileText className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" aria-hidden />
          <p className="text-muted-foreground">Nenhum contrato cadastrado.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {items.map((c) => {
            const pct = c.value_total > 0 ? Math.min(100, Math.round((c.paid / c.value_total) * 100)) : 0;
            return (
              <Card key={c.id} className="p-6 border-border/60 shadow-none rounded-md" data-testid={`contract-card-${c.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <h3 className="font-head font-bold text-lg tracking-tight">{c.title}</h3>
                    <p className="text-sm text-muted-foreground">{c.client_name || "Sem cliente"} · {c.installments_count || c.installments} parcela(s) · {FREQ[c.frequency] || c.frequency}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {c.attachment_path && (
                      <>
                        <a href={authFileUrl(c.attachment_path)} target="_blank" rel="noreferrer" title="Ver contrato" data-testid={`contract-file-${c.id}`}><Paperclip className="h-4 w-4 text-muted-foreground hover:text-primary" /></a>
                        <a href={authFileDownloadUrl(c.attachment_path)} title="Baixar contrato" data-testid={`contract-download-${c.id}`}><Download className="h-4 w-4 text-muted-foreground hover:text-primary" /></a>
                      </>
                    )}
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`delete-contract-${c.id}`} onClick={() => setToDelete(c)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-muted-foreground">Pago {BRL(c.paid)}</span>
                  <span className="font-semibold tnum">{BRL(c.value_total)}</span>
                </div>
                <Progress value={pct} className="h-2" />
                <p className="text-xs text-muted-foreground mt-2">Falta {BRL(c.remaining)}</p>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-auto" data-testid="contract-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl tracking-tight">Novo contrato</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Cliente</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="contract-client-select"><SelectValue placeholder="Selecione (opcional)" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Título / Nº do contrato</Label><Input data-testid="contract-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex: Aluguel loja 2026" /></div>
            <div className="space-y-2">
              <Label>Categoria <span className="text-destructive">*</span></Label>
              <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v, category_name: (categories.find((c) => c.id === v) || {}).name || "" })}>
                <SelectTrigger data-testid="contract-category-select"><SelectValue placeholder="Selecione a categoria" /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}><span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: c.color }} />{c.name}</span></SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Descrição</Label><Textarea rows={2} data-testid="contract-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Valor da parcela/mensalidade (R$)</Label><Input type="number" step="0.01" data-testid="contract-value-input" value={form.value_total} onChange={(e) => setForm({ ...form, value_total: e.target.value })} className="tnum" placeholder="Valor de cada parcela" /></div>
              <div className="space-y-2"><Label>Nº de parcelas</Label><Input type="number" min="1" data-testid="contract-installments-input" value={form.installments} onChange={(e) => setForm({ ...form, installments: e.target.value })} /></div>
            </div>
            {form.value_total && (parseInt(form.installments) || 1) > 0 && (
              <p className="text-sm text-muted-foreground -mt-2" data-testid="contract-total-preview">
                Serão geradas <span className="font-semibold text-foreground">{parseInt(form.installments) || 1}</span> parcela(s) de <span className="font-semibold text-foreground">{BRL(parseFloat(form.value_total) || 0)}</span> · Total <span className="font-semibold text-foreground">{BRL((parseFloat(form.value_total) || 0) * (parseInt(form.installments) || 1))}</span>
              </p>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>1º vencimento</Label><Input type="date" data-testid="contract-firstdue-input" value={form.first_due_date} onChange={(e) => setForm({ ...form, first_due_date: e.target.value })} /></div>
              <div className="space-y-2">
                <Label>Frequência</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                  <SelectTrigger data-testid="contract-frequency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(FREQ).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>CPF/CNPJ</Label><Input data-testid="contract-cpf-input" value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} /></div>
              <div className="space-y-2"><Label>Telefone</Label><Input data-testid="contract-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>Chave PIX</Label><Input data-testid="contract-pix-input" value={form.pix_key} onChange={(e) => setForm({ ...form, pix_key: e.target.value })} /></div>
            <div className="space-y-2">
              <Label>Arquivo do contrato (PDF/imagem)</Label>
              <label htmlFor="ctfile" className="flex items-center gap-2 px-3 py-2.5 rounded-md border border-dashed border-border cursor-pointer hover:bg-accent transition-colors text-sm">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4 text-muted-foreground" />}
                <span className="truncate">{attachment.name || "Anexar arquivo"}</span>
              </label>
              <input id="ctfile" type="file" accept="image/*,application/pdf" className="hidden" data-testid="contract-attachment-input" onChange={(e) => upload(e.target.files?.[0])} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving || uploading} data-testid="contract-save-button" className="font-semibold">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Criar e gerar parcelas"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-contract-dialog">
          <AlertDialogHeader><AlertDialogTitle>Remover contrato?</AlertDialogTitle><AlertDialogDescription>"{toDelete?.title}" e TODAS as suas parcelas serão removidas.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete-contract">Remover</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
